import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import type { CreateTemplateTaskForm, TemplateStep } from "@/types/task";
import type { Role } from "@/types/rbac";
import type { BasemapConfig } from "@/types/layers";
import { useAuth } from "@/hooks/useAuth";
import { RoleGuard } from "@/components/auth";
import {
  TemplateManager,
  TeamManager,
  UserManager,
  LayerManager,
  BasemapManager,
} from "@/components/forms";
import {
  InviteUserManagement,
  INVITES_QUERY_KEY,
} from "./InviteUserManagement";
import { useQueryClient } from "@tanstack/react-query";
import { InviteService } from "@/services/inviteService";
import { RBACService } from "@/services/rbacService";
import { UserService } from "@/services/userService";
import { RoleManagement } from "./RoleManagement";
import { useTeamStore } from "@/stores/teamStore";
import { useTemplateStore } from "@/stores/templateStore";
import { useLayerStore } from "@/stores/layerStore";
import { useModalStore } from "@/stores/modalStore";
import {
  TbUsers,
  TbUsersGroup,
  TbTemplate,
  TbChevronRight,
  TbX,
  TbArrowLeft,
  TbMap,
  TbShieldLock,
  TbWorld,
} from "react-icons/tb";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { ConfirmationModal } from "@/components/ui";

// Import React Query hooks
import { useAllUsers, userKeys } from "@/hooks/useUsers";
import { permissionsKeys } from "@/hooks/usePermissions";
import {
  useTeams,
  useAllTeamMembers,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useAddTeamMember,
  useRemoveTeamMember,
} from "@/hooks/useTeams";
import {
  useTemplates,
  useLookupValues,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from "@/hooks/useTasks";

interface AdminManagementProps {
  open: boolean;
  onClose: () => void;
}

export const AdminManagement: React.FC<AdminManagementProps> = ({
  open,
  onClose,
}) => {
  const { userProfile } = useAuth();
  const queryClient = useQueryClient();

  // Prefetch data when dialog opens
  useEffect(() => {
    if (open) {
      // Prefetch current user's permissions (needed for canManageRoles check)
      if (userProfile?.id) {
        queryClient.prefetchQuery({
          queryKey: permissionsKeys.byUserId(userProfile.id),
          queryFn: async () => {
            const userWithRoles = await RBACService.getUserWithRoles(
              userProfile.id
            );
            return {
              permissions: userWithRoles.permissions,
              roles: userWithRoles.roles,
            };
          },
          staleTime: 60000,
        });
      }

      // Prefetch users for UserManager (first tab)
      queryClient.prefetchQuery({
        queryKey: userKeys.allIncludingAdmins,
        queryFn: UserService.getAllUsersIncludingAdmins,
        staleTime: 30000,
      });

      // Prefetch roles for UserManager and InviteUserManagement
      queryClient.prefetchQuery({
        queryKey: ["roles"],
        queryFn: RBACService.getRoles,
        staleTime: 60000, // Roles don't change often, cache for 1 minute
      });

      // Prefetch invites
      queryClient.prefetchQuery({
        queryKey: INVITES_QUERY_KEY,
        queryFn: async () => {
          const result = await InviteService.getInvites();
          if (result.error) {
            throw new Error(result.error);
          }
          return result.invites || [];
        },
        staleTime: 30000,
      });
    }
  }, [open, queryClient, userProfile?.id]);

  // Zustand store for team state
  const { showCreateTeam, editingTeam, cancelTeamForm } = useTeamStore();

  // Zustand store for template state
  const { cancelTemplateForm } = useTemplateStore();

  // Zustand store for layer state
  const { resetLayerForm } = useLayerStore();

  // Modal state from modalStore
  const {
    showCreateTemplate,
    editingTemplate,
    showTemplateSteps,
    closeTemplateSteps,
    closeEditTemplate,
    closeCreateTemplate,
    showCreateLayer,
    editingLayer,
    closeEditLayer,
    closeCreateLayer,
  } = useModalStore();

  const [activeTab, setActiveTab] = useState<
    | "users"
    | "teams"
    | "templates"
    | "layers"
    | "basemaps"
    | "invitations"
    | "roles"
  >("users");
  const [deleteTeamDialog, setDeleteTeamDialog] = useState<{
    open: boolean;
    teamId: string | null;
  }>({ open: false, teamId: null });
  const [removeMemberDialog, setRemoveMemberDialog] = useState<{
    open: boolean;
    memberId: string | null;
  }>({ open: false, memberId: null });
  const [deleteTemplateDialog, setDeleteTemplateDialog] = useState<{
    open: boolean;
    templateId: string | null;
  }>({ open: false, templateId: null });
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRoleState, setEditingRoleState] = useState<Role | null>(null);
  const [showBasemapForm, setShowBasemapForm] = useState(false);
  const [editingBasemapState, setEditingBasemapState] =
    useState<BasemapConfig | null>(null);

  // Ref to hold RoleManagement's back handler
  const roleBackRef = useRef<(() => void) | undefined>(undefined);
  // Ref to hold BasemapManager's back handler
  const basemapBackRef = useRef<(() => void) | undefined>(undefined);

  // Memoized tabs array to prevent re-renders
  const tabs = useMemo<{ value: string; label: string; icon: ReactNode }[]>(
    () => [
      { value: "users", label: "Users", icon: <TbUsers className="w-4 h-4" /> },
      {
        value: "teams",
        label: "Teams",
        icon: <TbUsersGroup className="w-4 h-4" />,
      },
      {
        value: "templates",
        label: "Templates",
        icon: <TbTemplate className="w-4 h-4" />,
      },
      { value: "layers", label: "Layers", icon: <TbMap className="w-4 h-4" /> },
      {
        value: "basemaps",
        label: "Basemaps",
        icon: <TbWorld className="w-4 h-4" />,
      },
      {
        value: "invitations",
        label: "Invitations",
        icon: <TbUsers className="w-4 h-4" />,
      },
      {
        value: "roles",
        label: "Role Security",
        icon: <TbShieldLock className="w-4 h-4" />,
      },
    ],
    []
  );

  // React Query hooks for data fetching
  const { data: users = [], error: usersError } = useAllUsers();
  const { data: teams = [], error: teamsError } = useTeams();
  const { data: teamMembers = [], error: teamMembersError } =
    useAllTeamMembers();
  const { data: templates = [], error: templatesError } = useTemplates();
  const { data: categories = [], error: categoriesError } =
    useLookupValues("category");
  const { data: statuses = [], error: statusesError } =
    useLookupValues("task_status");
  const { data: priorities = [], error: prioritiesError } =
    useLookupValues("priority");

  // React Query mutations
  const createTeamMutation = useCreateTeam();
  const updateTeamMutation = useUpdateTeam();
  const deleteTeamMutation = useDeleteTeam();
  const addTeamMemberMutation = useAddTeamMember();
  const removeTeamMemberMutation = useRemoveTeamMember();
  const createTemplateMutation = useCreateTemplate();
  const updateTemplateMutation = useUpdateTemplate();
  const deleteTemplateMutation = useDeleteTemplate();

  // Compute loading and error states
  const loading = false; // React Query handles loading states automatically
  const error =
    usersError?.message ||
    teamsError?.message ||
    teamMembersError?.message ||
    templatesError?.message ||
    categoriesError?.message ||
    statusesError?.message ||
    prioritiesError?.message ||
    createTeamMutation.error?.message ||
    updateTeamMutation.error?.message ||
    deleteTeamMutation.error?.message ||
    addTeamMemberMutation.error?.message ||
    removeTeamMemberMutation.error?.message ||
    createTemplateMutation.error?.message ||
    updateTemplateMutation.error?.message ||
    deleteTemplateMutation.error?.message ||
    null;

  // Breadcrumb navigation handler
  const handleBreadcrumbNavigation = useCallback(
    (level: "management" | "teams" | "templates" | "layers" | "roles") => {
      if (level === "management") {
        // Reset to management overview - close all modals and go to users tab
        setActiveTab("users");
        cancelTeamForm();
        closeCreateTemplate();
        closeEditTemplate();
        closeCreateLayer();
        closeEditLayer();
        setShowRoleForm(false);
        setEditingRoleState(null);
      } else if (level === "teams") {
        // Go back to teams list
        cancelTeamForm();
      } else if (level === "templates") {
        // Go back to templates list - close template modals
        closeCreateTemplate();
        closeEditTemplate();
        if (showTemplateSteps) {
          closeTemplateSteps();
        }
        cancelTemplateForm();
      } else if (level === "layers") {
        // Go back to layers list - close layer modals
        closeCreateLayer();
        closeEditLayer();
        resetLayerForm();
      } else if (level === "roles") {
        // Go back to roles list
        setShowRoleForm(false);
        setEditingRoleState(null);
      }
    },
    [
      cancelTeamForm,
      closeCreateTemplate,
      closeEditTemplate,
      closeCreateLayer,
      closeEditLayer,
      closeTemplateSteps,
      showTemplateSteps,
      cancelTemplateForm,
      resetLayerForm,
    ]
  );

  const handleBackToTeams = useCallback(() => {
    cancelTeamForm();
  }, [cancelTeamForm]);

  const handleBackToTemplates = useCallback(() => {
    if (showTemplateSteps) {
      // If on steps page, go back to template form
      closeTemplateSteps();
    } else {
      // If on template form, go back to templates list
      cancelTemplateForm();
      closeEditTemplate();
      closeCreateTemplate();
    }
  }, [
    cancelTemplateForm,
    showTemplateSteps,
    closeTemplateSteps,
    closeEditTemplate,
    closeCreateTemplate,
  ]);

  const handleBackToLayers = useCallback(() => {
    resetLayerForm();
    closeEditLayer();
    closeCreateLayer();
  }, [resetLayerForm, closeEditLayer, closeCreateLayer]);

  const handleBackToRoles = useCallback(() => {
    // Call RoleManagement's reset function if available
    if (roleBackRef.current) {
      roleBackRef.current();
    }
    setShowRoleForm(false);
    setEditingRoleState(null);
  }, []);

  const handleBackToBasemaps = useCallback(() => {
    // Call BasemapManager's reset function if available
    if (basemapBackRef.current) {
      basemapBackRef.current();
    }
    setShowBasemapForm(false);
    setEditingBasemapState(null);
  }, []);

  const createTeam = async (teamData: {
    name: string;
    description: string;
  }) => {
    if (!teamData.name.trim()) return;

    createTeamMutation.mutate({
      name: teamData.name.trim(),
      description: teamData.description?.trim(),
    });
  };

  const updateTeam = async (
    teamId: string,
    teamData: {
      name: string;
      description: string;
    }
  ) => {
    if (!teamData.name.trim()) return;

    updateTeamMutation.mutate({
      teamId,
      teamData: {
        name: teamData.name.trim(),
        description: teamData.description?.trim(),
      },
    });
  };

  const deleteTeam = async (teamId: string) => {
    setDeleteTeamDialog({ open: true, teamId });
  };

  const confirmDeleteTeam = async (teamId: string) => {
    deleteTeamMutation.mutate(teamId, {
      onSuccess: () => {
        setDeleteTeamDialog({ open: false, teamId: null });
      },
    });
  };

  const addTeamMember = async (
    teamId: string,
    userId: string,
    role: "admin" | "member" = "member"
  ) => {
    addTeamMemberMutation.mutate({ teamId, userId, role });
  };

  const removeTeamMember = async (teamMemberId: string) => {
    setRemoveMemberDialog({ open: true, memberId: teamMemberId });
  };

  const confirmRemoveTeamMember = async (teamMemberId: string) => {
    removeTeamMemberMutation.mutate(teamMemberId, {
      onSuccess: () => {
        setRemoveMemberDialog({ open: false, memberId: null });
      },
    });
  };

  // Template management functions
  const createTemplate = async (
    templateData: CreateTemplateTaskForm,
    templateSteps: TemplateStep[]
  ) => {
    if (!templateData.name.trim() || !templateData.layer_name) return;

    createTemplateMutation.mutate({
      templateData: {
        name: templateData.name.trim(),
        description: templateData.description?.trim(),
        layer_name: templateData.layer_name,
        category_id: templateData.category_id || undefined,
        priority_id: templateData.priority_id || undefined,
        status_id: templateData.status_id || undefined,
        default_assignee_type: templateData.default_assignee_type,
        default_assignee: templateData.default_assignee,
        default_due_days: templateData.default_due_days,
        default_start_days: templateData.default_start_days,
        auto_assign_steps: templateData.auto_assign_steps,
        require_photos: templateData.require_photos,
        require_geometry: templateData.require_geometry,
        allowed_geometry_types: templateData.allowed_geometry_types,
      },
      templateSteps,
    });
  };

  const updateTemplate = async (
    templateId: string,
    templateData: CreateTemplateTaskForm,
    templateSteps: TemplateStep[]
  ) => {
    if (!templateData.layer_name) {
      throw new Error("Layer name is required to update the template");
    }

    updateTemplateMutation.mutate({
      templateId,
      templateData: {
        ...templateData,
        layer_name: templateData.layer_name as string,
      },
      templateSteps,
    });
  };

  const deleteTemplate = async (templateId: string) => {
    setDeleteTemplateDialog({ open: true, templateId });
  };

  const confirmDeleteTemplate = async (templateId: string) => {
    deleteTemplateMutation.mutate(templateId, {
      onSuccess: () => {
        setDeleteTemplateDialog({ open: false, templateId: null });
      },
    });
  };

  return (
    <RoleGuard adminOnly fallback={null}>
      <Dialog.Root open={open} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Content
            className="fixed top-20 right-4 left-4 md:left-auto bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] p-4 md:p-6 w-auto md:w-[780px] max-h-[calc(100vh-120px)] overflow-hidden z-[1000]"
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <div className="flex flex-col justify-between items-center h-full">
              <div className="flex flex-col items-start gap-6 w-full">
                <div className="flex w-full justify-between items-center">
                  <div className="flex items-center gap-2">
                    {/* Back Button - Show when creating or editing teams/templates */}
                    {activeTab === "teams" &&
                      (showCreateTeam || editingTeam) && (
                        <button
                          onClick={handleBackToTeams}
                          className="flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors"
                        >
                          <TbArrowLeft className="w-4 h-4 text-[#828282]" />
                        </button>
                      )}
                    {activeTab === "templates" &&
                      (showCreateTemplate || editingTemplate) && (
                        <button
                          onClick={handleBackToTemplates}
                          className="flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors"
                        >
                          <TbArrowLeft className="w-4 h-4 text-[#828282]" />
                        </button>
                      )}
                    {activeTab === "layers" &&
                      (showCreateLayer || editingLayer) && (
                        <button
                          onClick={handleBackToLayers}
                          className="flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors"
                        >
                          <TbArrowLeft className="w-4 h-4 text-[#828282]" />
                        </button>
                      )}
                    {activeTab === "roles" &&
                      !!(showRoleForm || editingRoleState) && (
                        <button
                          onClick={handleBackToRoles}
                          className="flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors"
                        >
                          <TbArrowLeft className="w-4 h-4 text-[#828282]" />
                        </button>
                      )}
                    {activeTab === "basemaps" &&
                      (showBasemapForm || editingBasemapState) && (
                        <button
                          onClick={handleBackToBasemaps}
                          className="flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors"
                        >
                          <TbArrowLeft className="w-4 h-4 text-[#828282]" />
                        </button>
                      )}
                    <div className="flex flex-col justify-center items-start">
                      {/* Breadcrumbs */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            handleBreadcrumbNavigation("management")
                          }
                          className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                        >
                          Management
                        </button>
                        <TbChevronRight className="w-3 h-3 text-[#828282]" />
                        {activeTab === "teams" &&
                        (showCreateTeam || editingTeam) ? (
                          <>
                            <button
                              onClick={() =>
                                handleBreadcrumbNavigation("teams")
                              }
                              className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                            >
                              Teams
                            </button>
                            <TbChevronRight className="w-3 h-3 text-[#828282]" />
                            <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                              {editingTeam ? "Edit Teams" : "Add Teams"}
                            </span>
                          </>
                        ) : activeTab === "templates" &&
                          (showCreateTemplate || editingTemplate) ? (
                          <>
                            <button
                              onClick={() =>
                                handleBreadcrumbNavigation("templates")
                              }
                              className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                            >
                              Templates
                            </button>
                            <TbChevronRight className="w-3 h-3 text-[#828282]" />
                            {showTemplateSteps ? (
                              <>
                                <button className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] transition-colors">
                                  {editingTemplate
                                    ? "Edit Templates"
                                    : "Add Templates"}
                                </button>
                                <TbChevronRight className="w-3 h-3 text-[#828282]" />
                                <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                                  Steps
                                </span>
                              </>
                            ) : (
                              <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                                {editingTemplate
                                  ? "Edit Templates"
                                  : "Add Templates"}
                              </span>
                            )}
                          </>
                        ) : activeTab === "layers" &&
                          (showCreateLayer || editingLayer) ? (
                          <>
                            <button
                              onClick={() =>
                                handleBreadcrumbNavigation("layers")
                              }
                              className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                            >
                              Asset Layers
                            </button>
                            <TbChevronRight className="w-3 h-3 text-[#828282]" />
                            <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                              {editingLayer ? "Edit Layer" : "Add Layer"}
                            </span>
                          </>
                        ) : activeTab === "roles" &&
                          (showRoleForm || editingRoleState) ? (
                          <>
                            <button
                              onClick={() =>
                                handleBreadcrumbNavigation("roles")
                              }
                              className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                            >
                              Role Security
                            </button>
                            <TbChevronRight className="w-3 h-3 text-[#828282]" />
                            <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                              {editingRoleState ? "Edit Role" : "Add Role"}
                            </span>
                          </>
                        ) : activeTab === "basemaps" &&
                          (showBasemapForm || editingBasemapState) ? (
                          <>
                            <button
                              onClick={() => handleBackToBasemaps()}
                              className="text-[#828282] font-['Open_Sans'] text-[10px] font-medium leading-[15px] hover:text-gray-600 cursor-pointer transition-colors"
                            >
                              Basemaps
                            </button>
                            <TbChevronRight className="w-3 h-3 text-[#828282]" />
                            <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                              {editingBasemapState
                                ? "Edit Basemap"
                                : "Add Basemap"}
                            </span>
                          </>
                        ) : (
                          <span className="text-[#444] font-['Open_Sans'] text-[12px] font-bold leading-[18px]">
                            {activeTab.charAt(0).toUpperCase() +
                              activeTab.slice(1)}
                          </span>
                        )}
                      </div>{" "}
                      {/* Title */}
                      <Dialog.Title className="justify-start custom-text-onyx text-xl font-bold leading-loose">
                        {activeTab === "teams" && showCreateTeam
                          ? "Create New Teams"
                          : activeTab === "teams" && editingTeam
                            ? "Edit Teams"
                            : activeTab === "templates" && showCreateTemplate
                              ? showTemplateSteps
                                ? "Template Steps"
                                : "Create New Template"
                              : activeTab === "templates" && editingTemplate
                                ? showTemplateSteps
                                  ? "Template Steps"
                                  : "Edit Template"
                                : activeTab === "layers" && showCreateLayer
                                  ? "Add New Layer"
                                  : activeTab === "layers" && editingLayer
                                    ? "Edit Layer"
                                    : activeTab === "roles" && editingRoleState
                                      ? "Editing"
                                      : activeTab === "roles" && showRoleForm
                                        ? "Create New Role"
                                        : activeTab === "basemaps" &&
                                            editingBasemapState
                                          ? "Edit Basemap"
                                          : activeTab === "basemaps" &&
                                              showBasemapForm
                                            ? "Add New Basemap"
                                            : "Management"}
                      </Dialog.Title>
                    </div>
                  </div>
                  {!showCreateTeam &&
                    !editingTeam &&
                    !showCreateTemplate &&
                    !editingTemplate &&
                    !showCreateLayer &&
                    !editingLayer &&
                    !showRoleForm &&
                    !editingRoleState &&
                    !showBasemapForm &&
                    !editingBasemapState && (
                      <Dialog.Close asChild>
                        <button className="z-2001 flex w-[45px] h-[45px] p-3.5 justify-center cursor-pointer items-center gap-2.5 rounded-[42px] bg-[#F3F4F6] hover:bg-gray-300 transition-colors">
                          <TbX className="w-4 h-4 text-[#828282]" />
                        </button>
                      </Dialog.Close>
                    )}
                </div>

                <Dialog.Description className="sr-only">
                  Administrative interface for managing users, teams, templates,
                  and access requests
                </Dialog.Description>

                <div className="flex w-full flex-col items-start gap-3">
                  {/* Tabs */}
                  <Tabs.Root
                    value={activeTab}
                    onValueChange={(value) =>
                      setActiveTab(
                        value as
                          | "users"
                          | "teams"
                          | "templates"
                          | "layers"
                          | "basemaps"
                          | "invitations"
                          | "roles"
                      )
                    }
                    className="w-full"
                  >
                    <Tabs.List className="flex items-center w-full border-b border-[#F3F4F6] overflow-x-auto scrollbar-hide">
                      {tabs.map((tab) => (
                        <Tabs.Trigger
                          key={tab.value}
                          value={tab.value}
                          className="flex pb-3.5 px-3 justify-center items-center border-b-2 border-transparent data-[state=active]:border-[#374795] hover:text-gray-700 whitespace-nowrap flex-shrink-0"
                        >
                          <div
                            className={`flex items-center gap-1 text-[13px] leading-[19.6px] ${
                              activeTab === tab.value
                                ? "text-[#374795]"
                                : "text-[#828282]"
                            }`}
                          >
                            {tab.icon}
                            <span className="font-semibold">{tab.label}</span>
                          </div>
                        </Tabs.Trigger>
                      ))}
                    </Tabs.List>
                  </Tabs.Root>

                  {/* Content Area */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded w-full">
                      {error}
                    </div>
                  )}

                  {loading ? (
                    <div className="flex justify-center py-8 w-full">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Users Tab */}
                      {activeTab === "users" && (
                        <div className="w-full">
                          <UserManager
                            users={users}
                            userProfile={userProfile}
                            error={usersError?.message}
                          />
                        </div>
                      )}

                      {/* Teams Tab */}
                      {activeTab === "teams" && (
                        <div className="w-full h-full">
                          <TeamManager
                            teams={teams}
                            teamMembers={teamMembers}
                            users={users}
                            onCreateTeam={createTeam}
                            onUpdateTeam={updateTeam}
                            onDeleteTeam={deleteTeam}
                            onAddTeamMember={addTeamMember}
                            onRemoveTeamMember={removeTeamMember}
                            error={error}
                          />
                        </div>
                      )}

                      {/* Templates Tab */}
                      {activeTab === "templates" && (
                        <div className="w-full">
                          <TemplateManager
                            templates={templates}
                            categories={categories}
                            statuses={statuses}
                            priorities={priorities}
                            users={users}
                            teams={teams}
                            onCreateTemplate={createTemplate}
                            onUpdateTemplate={updateTemplate}
                            onDeleteTemplate={deleteTemplate}
                            error={error}
                          />
                        </div>
                      )}

                      {/* Layers Tab */}
                      {activeTab === "layers" && (
                        <div className="w-full">
                          <LayerManager error={error || undefined} />
                        </div>
                      )}

                      {/* Basemaps Tab */}
                      {activeTab === "basemaps" && (
                        <div className="w-full">
                          <BasemapManager
                            error={error || undefined}
                            onFormStateChange={(isFormOpen, editingBasemap) => {
                              setShowBasemapForm(isFormOpen);
                              setEditingBasemapState(editingBasemap);
                            }}
                            onBackRef={basemapBackRef}
                          />
                        </div>
                      )}

                      {/* User Invitations Tab */}
                      {activeTab === "invitations" && (
                        <div className="w-full">
                          <InviteUserManagement />
                        </div>
                      )}

                      {/* Roles Tab */}
                      {activeTab === "roles" && (
                        <div className="w-full">
                          <RoleManagement
                            onFormStateChange={(isFormOpen, editingRole) => {
                              setShowRoleForm(isFormOpen);
                              setEditingRoleState(editingRole);
                            }}
                            onBackRef={roleBackRef}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Confirmation Modals */}
            <ConfirmationModal
              open={deleteTeamDialog.open}
              onOpenChange={(open) =>
                setDeleteTeamDialog({ open, teamId: null })
              }
              title="Delete Team"
              description="Are you sure you want to delete this team? All team members will be removed. This action cannot be undone."
              onConfirm={() =>
                deleteTeamDialog.teamId &&
                confirmDeleteTeam(deleteTeamDialog.teamId)
              }
            />

            <ConfirmationModal
              open={removeMemberDialog.open}
              onOpenChange={(open) =>
                setRemoveMemberDialog({ open, memberId: null })
              }
              title="Remove Team Member"
              description="Are you sure you want to remove this member from the team? This action cannot be undone."
              onConfirm={() =>
                removeMemberDialog.memberId &&
                confirmRemoveTeamMember(removeMemberDialog.memberId)
              }
            />

            <ConfirmationModal
              open={deleteTemplateDialog.open}
              onOpenChange={(open) =>
                setDeleteTemplateDialog({ open, templateId: null })
              }
              title="Delete Template"
              description="Are you sure you want to delete this template? This action cannot be undone."
              onConfirm={() =>
                deleteTemplateDialog.templateId &&
                confirmDeleteTemplate(deleteTemplateDialog.templateId)
              }
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </RoleGuard>
  );
};
